/**
 * Organisation program API.
 *
 * Own module, following peerAdvisoryApi.ts / paymentsApi.ts, so the new surface
 * stays reviewable on its own. camelCase on the wire: every backend schema is a
 * Pydantic CamelModel (schemas/organisation_program.py), so each type below
 * mirrors one of them field-for-field (OrganisationProgramOut,
 * OrganisationPlanOut, PoolMemberOut, ...).
 *
 * NOTHING DERIVED IS COMPUTED HERE OR IN THE UI. Labels ("NS1", "OKR1",
 * "KR1", "KI-3"), counts, per-step readiness, the publish checklist and the
 * viewer's update rights all come from the server. Every plan mutation returns
 * the whole updated plan, which callers write straight into the query cache.
 *
 * Sessions are READ from the plan (`plan.sessions`, one request for the whole
 * page) and WRITTEN through the type-agnostic session endpoints
 * (programsApi.createSession / updateSession / deleteSession) with
 * `org_kind` / `okr_id`, which apply the Organisation rules server-side.
 */

import { authAxios } from "@/services/axiosInstance";
import type { MembershipRole, ProgramStatus } from "@/services/api";
import { getEnvVar } from "@/utils/util";

const API_BASE = getEnvVar("API_BASE");
const ORG_BASE = `${API_BASE}/organisation-programs`;

// ── Types (one per backend Pydantic schema) ───────────────────────────────────

/** Mirrors the backend's TaskStatus, reused for key results and initiatives. */
export type OrgItemStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "NOT_REQUIRED" | "MISSED";

export type OrgMembershipStatus = "PENDING" | "ACTIVE" | "REMOVED" | "COMPLETED";

/** Prefills the cycle window: ANNUAL = one year, QUARTERLY = three months. */
export type OrgCycleType = "ANNUAL" | "QUARTERLY";

/** Wizard steps; the server's checklist `step` values are the first five. */
export type OrgWizardStep =
  | "overview"
  | "north-stars"
  | "objectives"
  | "initiatives"
  | "sessions"
  | "review";

/** EligibilityOut */
export interface OrgEligibility {
  canCreate: boolean;
}

/** ActionResultOut */
export interface OrgActionResult {
  externalId: string;
  detail: string;
}

/** PoolMemberOut */
export interface OrgPoolMember {
  membershipId: number;
  /** Null for someone invited who has no account yet (known by email only). */
  userId: string | null;
  /** Full name, or the email when the account has no name -- server-resolved. */
  fullName: string;
  email: string;
  avatarUrl: string | null;
  /** LEAD = the program's creator; everyone else MEMBER. Rights come from
   *  `isAdmin`, not from this. */
  role: MembershipRole;
  status: OrgMembershipStatus;
  /** One of the program's admins: can change anything in it. */
  isAdmin: boolean;
  hasAccount: boolean;
  /** "Achiever" / "Expert" / "Admin" -- shown beside the name. Empty until an invitee accepts. */
  personas: string[];
}

/** DomainUserOut */
export interface OrgDomainUser {
  userId: string;
  fullName: string;
  email: string;
  avatarUrl: string | null;
  inPool: boolean;
  /** Their membership status when in the program; PENDING = invited, not accepted. */
  poolStatus: OrgMembershipStatus | null;
  /** "Achiever" / "Expert" / "Admin" -- shown beside the name. */
  personas: string[];
}

/** OrgInvitationOut -- an invitation nobody has answered yet (admin view). */
export interface OrgInvitation {
  id: number;
  /** The invited address; the account's name isn't shown until they accept. */
  email: string;
  hasAccount: boolean;
  membershipId: number | null;
  /** Server-computed: NOT_SENT until launch, then PENDING, or EXPIRED. */
  status: "NOT_SENT" | "PENDING" | "EXPIRED";
  invitedAt: string;
  expiresAt: string | null;
}

/** ChecklistItemOut */
export interface OrgChecklistItem {
  id: string;
  label: string;
  satisfied: boolean;
  /** The wizard step that fixes it. */
  step: OrgWizardStep;
}

/** OrgReadinessOut -- per-step completion; the wizard's only gate. Nobody
 *  has to have accepted an invitation for the program to launch. */
export interface OrgReadiness {
  overview: boolean;
  northStars: boolean;
  objectives: boolean;
  /** Always true: OKIs can be added before or after launch. */
  initiatives: boolean;
  sessions: boolean;
  readyToSubmit: boolean;
  checklist: OrgChecklistItem[];
}

/** OrganisationProgramOut */
export interface OrganisationProgramDetail {
  id: number;
  externalId: string;
  programType: "ORGANISATION";
  status: ProgramStatus;
  /** The Organisation Name (programs.name). */
  name: string;
  description: string | null;
  emailDomain: string | null;
  cadenceSummary: string | null;
  cycleType: OrgCycleType | null;
  startsOn: string | null;
  endsOn: string | null;
  isPublicDomain: boolean;
  slug: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  myRole: MembershipRole | null;
  northStarsCount: number;
  objectivesCount: number;
  initiativesCount: number;
  sessionsCount: number;
  membersCount: number;
  readiness: OrgReadiness;
  submittedAt: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

/** GroupedOrganisationProgramsOut */
export interface GroupedOrganisationPrograms {
  active: OrganisationProgramDetail[];
  draft: OrganisationProgramDetail[];
  inReview: OrganisationProgramDetail[];
  rejected: OrganisationProgramDetail[];
  past: OrganisationProgramDetail[];
}

/** NorthStarRefOut -- a North Star as tagged on an objective. */
export interface OrgNorthStarRef {
  id: number;
  label: string;
}

/** NorthStarKeyResultOut */
export interface OrgNorthStarKeyResult {
  id: number;
  /** "KR1", "KR2"... within the North Star. */
  label: string;
  title: string;
  /** 0-100: its bar, moved by the North Star's owner or an admin. */
  progress: number;
}

/** NorthStarOut */
export interface OrgNorthStar {
  id: number;
  /** "NS1", "NS2"... by creation order. */
  label: string;
  /** Markdown, edited as rich text. */
  directionStatement: string;
  keyResults: OrgNorthStarKeyResult[];
  owner: OrgPoolMember | null;
  objectivesCount: number;
  /** Key initiatives under the objectives that serve it. */
  initiativesCount: number;
  /** 0-100: the average of its own key results (0 with none). */
  progress: number;
  /** All three: a program admin or this North Star's owner. */
  /** Edit, hand on, delete. */
  viewerCanEdit: boolean;
  /** Move its key results' bars. */
  viewerCanUpdate: boolean;
  /** Book its North Star check-ins. */
  viewerCanScheduleMeetings: boolean;
  /** Membership ids a new check-in for it invites (server rule). */
  checkInParticipantIds: number[];
}

/** KeyResultOut */
export interface OrgKeyResult {
  id: number;
  /** "KR1", "KR2"... within the objective. */
  label: string;
  title: string;
  /** 0-100: its progress bar (the OKR's driver and the admin move it). */
  progress: number;
}

/** ObjectiveOut */
export interface OrgObjective {
  id: number;
  /** The objective's code ("OKR1", "FSN"), or "" when it has none. */
  label: string;
  /** What the chip shows -- same as `label`; no chip when "". */
  chipLabel: string;
  code: string | null;
  title: string;
  status: string;
  /** 0-100: the average of its key results' progress (0 with none) -- the
   *  OKRs tab. Its OKIs play no part. */
  progress: number;
  /** 0-100: its OKIs' status score (Not started 0, In progress 50,
   *  Completed 100, averaged). */
  initiativesProgress: number;
  /** 0-100: the Overview's bar -- `progress` and `initiativesProgress`
   *  averaged, or `progress` alone with no OKIs. Server-computed. */
  overviewProgress: number;
  driver: OrgPoolMember | null;
  contributors: OrgPoolMember[];
  northStars: OrgNorthStarRef[];
  keyResults: OrgKeyResult[];
  initiativesCount: number;
  /** All four: a program admin or this objective's driver, at any time. */
  /** Edit / hand on / delete the objective itself. */
  viewerCanEdit: boolean;
  /** Update its key results' progress. */
  viewerCanUpdate: boolean;
  /** Add OKIs under it (each is then its own driver's to change). */
  viewerCanAddInitiatives: boolean;
  /** Book its OKR check-ins. */
  viewerCanScheduleMeetings: boolean;
  /** Membership ids a new check-in for it invites -- its driver, its OKIs'
   *  drivers and its North Stars' owners (server rule). */
  checkInParticipantIds: number[];
}

/** InitiativeOut */
export interface OrgInitiative {
  id: number;
  okrId: number;
  code: string | null;
  /** Its code ("OKI3") -- every OKI has one. */
  label: string;
  title: string;
  // SUPERSEDED (ownership round): an OKI has a status, not a progress bar.
  // /** 0-100: its progress bar. */
  // progress: number;
  /** Not started / In progress / Completed, as its driver sets it. It never
   *  moves its OKR (an OKR's progress is its key results'). */
  status: OrgItemStatus;
  driver: OrgPoolMember | null;
  contributors: OrgPoolMember[];
  /** All three: a program admin or its own driver (not its OKR's driver). */
  /** Set its status. */
  viewerCanUpdate: boolean;
  /** Edit / hand on / delete it. */
  viewerCanEdit: boolean;
  /** Book its OKI check-ins. */
  viewerCanScheduleMeetings: boolean;
  /** Membership ids a new check-in for it invites (server rule). */
  checkInParticipantIds: number[];
}

/**
 * KICKOFF / CHECKPOINT / RECAP: company-wide (at most one each, only the
 * Kickoff required; an admin books them; everyone in the program invited).
 * The rest are check-ins, any number per item:
 *   NORTH_STAR  its owner and the drivers of the OKRs serving it
 *   OKR         its driver and its OKIs' drivers, plus its North Stars'
 *               owners unless the organiser left them off
 *   OKI         its driver and contributors
 */
export type OrgSessionKind = "KICKOFF" | "CHECKPOINT" | "RECAP" | "NORTH_STAR" | "OKR" | "OKI";

/** OrgSessionOut */
export interface OrgSession {
  id: number;
  kind: OrgSessionKind;
  northStarId: number | null;
  okrId: number | null;
  okiId: number | null;
  /** OKR check-ins: whether its North Stars' owners are invited. */
  includeNorthStarOwners: boolean | null;
  title: string;
  description: string | null;
  startTime: string | null;
  endTime: string | null;
  /** The IANA zone it was picked in -- edited in that zone. */
  timezone: string | null;
  meetingUrl: string | null;
  /** False until launch books it (calendar event, Meet link, invites). */
  isBooked: boolean;
  participantsCount: number;
  /** Membership ids (in `pool`) of everyone it invites, by the calendar
   *  invite's own rule. */
  participantIds: number[];
  /** The Sessions tab's columns, split from `participantIds` by the server:
   *  who booked it, its owners / drivers, and everyone else it invites. */
  hostMembershipId: number | null;
  driverIds: number[];
  contributorIds: number[];
  /** Under its title: "Whole program", "NS1 · Revenue", "OKR1 · …". */
  subject: string;
  /** What it covers: "Plans shared, owners confirmed", "2 KRs · 1 KI"… */
  covers: string;
  viewerCanEdit: boolean;
}

/** MyOrgSessionOut -- a session on the caller's /sessions page (Organization
 *  tab), with its program and its people. */
export interface MyOrgSession extends OrgSession {
  programExternalId: string;
  programName: string;
  participants: OrgPoolMember[];
}

/** The 409 `detail` when new cycle dates would leave sessions outside it. */
export interface OrgSessionsOutsideCycle {
  code: "SESSIONS_OUTSIDE_CYCLE";
  message: string;
  sessions: { id: number; title: string; kind: OrgSessionKind; startTime: string | null }[];
}

/** OrgInsightOut -- one line of "Worth a look before kick-off". */
export interface OrgInsight {
  code: string;
  headline: string;
  detail: string;
}

/** OrgOwnershipLoadOut -- how much of the plan one person carries. */
export interface OrgOwnershipLoad {
  membershipId: number;
  objectivesDriven: number;
  initiativesDriven: number;
  objectivesContributing: number;
  initiativesContributing: number;
  /** Everything they are on, driving or contributing ("5 OKRs · 2 OKIs"). */
  objectives: number;
  initiatives: number;
  /** The People tab: codes of what they own / drive ("NS1", "OKR6", "KI-14")
   *  and of what they contribute to, in plan order. */
  drives: string[];
  contributes: string[];
  /** 2 x what they own / drive + what they contribute to; `loadShare` is
   *  that against the heaviest person's, 0-100 -- both the server's. */
  load: number;
  loadShare: number;
}

/** OrgNorthStarCoverageOut -- how many objectives serve one North Star. */
export interface OrgNorthStarCoverage {
  northStarId: number;
  objectives: number;
  /** 0-100: its share of all the program's objectives (the bar's length). */
  share: number;
}

/** OrgPlanSummaryOut -- the Overview's numbers, computed by the server. */
export interface OrgPlanSummary {
  /** The average of the OKRs' progress, 0-100. */
  overallProgress: number;
  /** "Week 3 of 13" while the cycle runs; null before it starts or after it ends. */
  week: number | null;
  weeksTotal: number | null;
  daysLeft: number | null;
  initiativesNotStarted: number;
  initiativesInProgress: number;
  initiativesCompleted: number;
  /** The next sessions still ahead, soonest first (at most three). */
  upcomingSessionIds: number[];
  /** Key results across every objective, and across every North Star -- the
   *  Overview's tiles never mix the two kinds. */
  objectiveKeyResults: number;
  northStarKeyResults: number;
  /** Both kinds together, for the numbers strip. */
  keyResults: number;
  /** North Stars at least one objective serves ("6 OKRs across 3 North Stars"). */
  northStarsWithObjectives: number;
  /** Objectives with at least one OKI ("16 key initiatives across 6 OKRs"). */
  objectivesWithInitiatives: number;
  /** What to look at before kick-off, most pressing first (at most four). */
  insights: OrgInsight[];
  /** Who carries what, heaviest first: everyone on at least one item. */
  ownershipLoad: OrgOwnershipLoad[];
  northStarCoverage: OrgNorthStarCoverage[];
}

/** OrganisationPlanOut */
export interface OrganisationPlan {
  northStars: OrgNorthStar[];
  objectives: OrgObjective[];
  initiatives: OrgInitiative[];
  /** Company-wide sessions first (Kickoff, Checkpoint, Recap), then OKR and
   *  OKI meetings, each by start time. */
  sessions: OrgSession[];
  /** The viewer's own membership (in `pool`) -- the organiser of a session
   *  they book. */
  viewerMembershipId: number | null;
  /** Whether the viewer is the program admin. */
  viewerIsAdmin: boolean;
  /** Whether the viewer may add North Stars and OKRs -- the admin, until the
   *  program has ended (then it is read-only for everyone). */
  viewerCanAddItems: boolean;
  /** Whether the viewer may add/edit the company-wide sessions. */
  viewerCanManageSessions: boolean;
  /** Everyone assigned or invited; status PENDING = invited, not accepted. */
  pool: OrgPoolMember[];
  /** Open invitations (the admin only). */
  invitations: OrgInvitation[];
  readiness: OrgReadiness;
  /** Code pre-filled for the next new objective / initiative ("OKR4", "OKI6"):
   *  one past the highest number already used with that prefix. */
  nextObjectiveLabel: string;
  nextInitiativeCode: string;
  /** The Overview tab's dashboard numbers. */
  summary: OrgPlanSummary;
}

// ── Request payloads (mirror the *Create / *Update schemas) ───────────────────

export interface OrgOverviewPayload {
  /** The Organisation Name. */
  name?: string;
  emailDomain?: string | null;
  description?: string | null;
  cadenceSummary?: string | null;
  cycleType?: OrgCycleType | null;
  startsOn?: string | null;
  endsOn?: string | null;
  /** Send after the user confirms the 409's list: those sessions are deleted
   *  (calendar invites cancelled, organisers and attendees notified). */
  confirmSessionRemoval?: boolean;
}

/**
 * People in a save are existing members (`…MembershipId(s)`) or emails
 * (`…Email(s)`): a colleague not in the program yet, or anyone else to invite
 * -- with or without an account. The server adds / invites the emailed people
 * in this same save, so nobody is added for a drawer that is cancelled, and
 * nothing is sent to anyone before launch.
 */
export interface OrgKeyResultPayload {
  /** Set to keep/rename an existing key result; omit to create one. */
  id?: number;
  title: string;
}

export interface OrgNorthStarPayload {
  directionStatement: string;
  keyResults: OrgKeyResultPayload[];
  ownerMembershipId: number | null;
  ownerEmail?: string | null;
}

export interface OrgObjectivePayload {
  code: string | null;
  title: string;
  /** Exactly one of driverMembershipId / driverEmail. */
  driverMembershipId: number | null;
  driverEmail?: string | null;
  contributorMembershipIds: number[];
  contributorEmails?: string[];
  northStarIds: number[];
  keyResults: OrgKeyResultPayload[];
}

export interface OrgInitiativePayload {
  okrId: number;
  code: string | null;
  title: string;
  /** Exactly one of driverMembershipId / driverEmail. */
  driverMembershipId: number | null;
  driverEmail?: string | null;
  contributorMembershipIds: number[];
  contributorEmails?: string[];
}

// ── API ───────────────────────────────────────────────────────────────────────

const url = (path: string) => `${ORG_BASE}/${path}`;

export const organisationProgramApi = {
  getEligibility: async (): Promise<OrgEligibility> => {
    const { data } = await authAxios.get(url("eligibility"));
    return data;
  },

  /** Programs the caller runs as admin, bucketed by state. */
  getGrouped: async (): Promise<GroupedOrganisationPrograms> => {
    const { data } = await authAxios.get(url("staff/grouped"));
    return data;
  },

  /** Live programs the caller belongs to, in any role. */
  getMine: async (): Promise<OrganisationProgramDetail[]> => {
    const { data } = await authAxios.get(url("me"));
    return data;
  },

  /** Sessions the caller is on, across their launched programs, by start time. */
  getMySessions: async (): Promise<MyOrgSession[]> => {
    const { data } = await authAxios.get(url("sessions/me"));
    return data;
  },

  create: async (
    payload: OrgOverviewPayload & { name: string },
  ): Promise<OrganisationProgramDetail> => {
    const { data } = await authAxios.post(ORG_BASE, payload);
    return data;
  },

  get: async (externalId: string): Promise<OrganisationProgramDetail> => {
    const { data } = await authAxios.get(url(externalId));
    return data;
  },

  update: async (
    externalId: string,
    payload: OrgOverviewPayload,
  ): Promise<OrganisationProgramDetail> => {
    const { data } = await authAxios.patch(url(externalId), payload);
    return data;
  },

  remove: async (externalId: string): Promise<OrgActionResult> => {
    const { data } = await authAxios.delete(url(externalId));
    return data;
  },

  /** DRAFT -> LIVE, by the admin. Sends every invitation and books every
   *  session; nobody has to have accepted anything first. */
  launch: async (externalId: string): Promise<OrganisationProgramDetail> => {
    const { data } = await authAxios.post(url(`${externalId}/launch`));
    return data;
  },

  getPlan: async (externalId: string): Promise<OrganisationPlan> => {
    const { data } = await authAxios.get(url(`${externalId}/plan`));
    return data;
  },

  // ── People pool ─────────────────────────────────────────────────────────

  searchDomainUsers: async (externalId: string, q: string): Promise<OrgDomainUser[]> => {
    const { data } = await authAxios.get(url(`${externalId}/domain-users`), {
      params: q ? { q } : undefined,
    });
    return data;
  },

  // ── Invitations (another domain, or no account yet) ─────────────────────
  // Created by the North Star / objective / initiative SAVE that assigns
  // someone by email, sent at launch (or straight away once launched) --
  // there is no "invite now" call, so a cancelled drawer contacts nobody.

  resendInvitation: async (externalId: string, invitationId: number): Promise<OrganisationPlan> => {
    const { data } = await authAxios.post(url(`${externalId}/invitations/${invitationId}/resend`));
    return data;
  },

  /** Only someone who hasn't accepted yet; their assignments are cleared. */
  withdrawInvitation: async (
    externalId: string,
    invitationId: number,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.delete(url(`${externalId}/invitations/${invitationId}`));
    return data;
  },

  /** The invitee's answer, from their Home requests. */
  respondToInvitation: async (
    externalId: string,
    invitationId: number,
    accept: boolean,
  ): Promise<{ accepted: boolean; externalId: string }> => {
    const { data } = await authAxios.post(
      url(`${externalId}/invitations/${invitationId}/respond`),
      { accept },
    );
    return data;
  },

  removeFromPool: async (externalId: string, membershipId: number): Promise<OrganisationPlan> => {
    const { data } = await authAxios.delete(url(`${externalId}/pool/${membershipId}`));
    return data;
  },

  // ── North Stars ─────────────────────────────────────────────────────────

  createNorthStar: async (
    externalId: string,
    payload: OrgNorthStarPayload,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.post(url(`${externalId}/north-stars`), payload);
    return data;
  },

  updateNorthStar: async (
    externalId: string,
    northStarId: number,
    payload: Partial<OrgNorthStarPayload>,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.patch(
      url(`${externalId}/north-stars/${northStarId}`),
      payload,
    );
    return data;
  },

  deleteNorthStar: async (externalId: string, northStarId: number): Promise<OrganisationPlan> => {
    const { data } = await authAxios.delete(url(`${externalId}/north-stars/${northStarId}`));
    return data;
  },

  // ── Objectives ──────────────────────────────────────────────────────────

  createObjective: async (
    externalId: string,
    payload: OrgObjectivePayload,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.post(url(`${externalId}/objectives`), payload);
    return data;
  },

  updateObjective: async (
    externalId: string,
    okrId: number,
    payload: Partial<OrgObjectivePayload>,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.patch(url(`${externalId}/objectives/${okrId}`), payload);
    return data;
  },

  deleteObjective: async (externalId: string, okrId: number): Promise<OrganisationPlan> => {
    const { data } = await authAxios.delete(url(`${externalId}/objectives/${okrId}`));
    return data;
  },

  updateKeyResultProgress: async (
    externalId: string,
    okrId: number,
    keyResultId: number,
    progress: number,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.patch(
      url(`${externalId}/objectives/${okrId}/key-results/${keyResultId}/progress`),
      { progress },
    );
    return data;
  },

  // ── Initiatives ─────────────────────────────────────────────────────────

  createInitiative: async (
    externalId: string,
    payload: OrgInitiativePayload,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.post(url(`${externalId}/initiatives`), payload);
    return data;
  },

  updateInitiative: async (
    externalId: string,
    initiativeId: number,
    payload: Partial<OrgInitiativePayload>,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.patch(
      url(`${externalId}/initiatives/${initiativeId}`),
      payload,
    );
    return data;
  },

  deleteInitiative: async (externalId: string, initiativeId: number): Promise<OrganisationPlan> => {
    const { data } = await authAxios.delete(url(`${externalId}/initiatives/${initiativeId}`));
    return data;
  },

  // SUPERSEDED (ownership round): an OKI has a status its driver sets, not a
  // progress bar (updateInitiativeStatus below).
  // updateInitiativeProgress: async (
  //   externalId: string,
  //   initiativeId: number,
  //   progress: number,
  // ): Promise<OrganisationPlan> => {
  //   const { data } = await authAxios.patch(
  //     url(`${externalId}/initiatives/${initiativeId}/progress`),
  //     { progress },
  //   );
  //   return data;
  // },

  /** An OKI's status -- its driver or an admin. Nothing else moves with it. */
  updateInitiativeStatus: async (
    externalId: string,
    initiativeId: number,
    status: OrgItemStatus,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.patch(
      url(`${externalId}/initiatives/${initiativeId}/status`),
      { status },
    );
    return data;
  },

  /** A North Star key result's bar -- its owner or an admin. */
  updateNorthStarKeyResultProgress: async (
    externalId: string,
    northStarId: number,
    keyResultId: number,
    progress: number,
  ): Promise<OrganisationPlan> => {
    const { data } = await authAxios.patch(
      url(`${externalId}/north-stars/${northStarId}/key-results/${keyResultId}/progress`),
      { progress },
    );
    return data;
  },

  // Program admins are managed in the database (org_program_admins), not
  // from the app -- no add / remove calls here.
};

/** React Query keys, in one place so invalidation never drifts. */
export const orgProgramKeys = {
  all: ["organisation-programs"] as const,
  grouped: () => [...orgProgramKeys.all, "grouped"] as const,
  mine: () => [...orgProgramKeys.all, "mine"] as const,
  mySessions: () => [...orgProgramKeys.all, "my-sessions"] as const,
  eligibility: () => [...orgProgramKeys.all, "eligibility"] as const,
  detail: (externalId: string) => [...orgProgramKeys.all, "detail", externalId] as const,
  plan: (externalId: string) => [...orgProgramKeys.all, "plan", externalId] as const,
  domainUsers: (externalId: string, q: string) =>
    [...orgProgramKeys.all, "domain-users", externalId, q] as const,
  /** Every cached search for a program -- invalidated when someone joins it. */
  domainUsersAll: (externalId: string) =>
    [...orgProgramKeys.all, "domain-users", externalId] as const,
};
